import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { isProjectorWindow, isMirroring, subscribeMirroring, useLiveDemoScene } from '@/components/admin/PresentationStage';

// ── Stage privacy ────────────────────────────────────────────────────────────
// While a demo session is live, the screen the ROOM sees must not show real
// personnel names — live scenes put real admin desks (terminations, rosters,
// leaderboards) on the projector, and cloned demo mirrors carry the source
// crew's real names inside their data, so no per-page filter can catch it all.
//
// This masks at the display layer instead: on the projector window (and on a
// screen deliberately in mirroring mode), every real subcontractor's name in
// rendered text becomes "First X." — the data underneath is untouched, the
// presenter's own laptop is never masked, and it switches off the moment the
// session ends. Names come from the non-demo subcontractor roster, so demo
// attendees' own names stay visible.

// Build one matcher from the roster. Only multi-word names are masked —
// single words over-match ordinary prose — and a name whose trailing words
// are ALREADY bare initials is skipped: its mask would still contain the
// name, and re-masking our own replacement would grow it forever.
const buildMasker = (rows) => {
  const map = new Map();
  for (const r of rows || []) {
    const candidates = [
      r.full_name,
      [r.first_name, r.last_name].filter(Boolean).join(' '),
      r.company_name,
    ];
    for (const raw of candidates) {
      const name = (raw || '').trim().replace(/\s+/g, ' ');
      const words = name.split(' ');
      if (words.length < 2) continue;
      const tail = words.slice(1);
      if (tail.every((w) => w.replace(/[.]/g, '').length <= 1)) continue;
      const masked = `${words[0]} ${tail.map((w) => `${w[0].toUpperCase()}.`).join(' ')}`;
      map.set(name.toLowerCase(), masked);
    }
  }
  if (map.size === 0) return null;
  const escaped = [...map.keys()]
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return { regex: new RegExp(`\\b(?:${escaped.join('|')})\\b`, 'gi'), map };
};

// The stage page (chromeless, outside AdminLayout) warms this during the
// lobby; when a live scene navigates the projector window onto a real admin
// page, the AdminLayout instance mounts with the matcher ALREADY built, and
// the layout-effect walk below masks before the first paint — no flash of a
// real name on the room's screen.
let maskerCache = null;
let maskerLoading = null;
const loadMasker = () => {
  if (maskerCache) return Promise.resolve(maskerCache);
  if (!maskerLoading) {
    maskerLoading = supabase
      .from('subcontractors')
      .select('first_name, last_name, full_name, company_name')
      .eq('is_demo', false)
      .then(({ data }) => {
        maskerCache = buildMasker(data);
        return maskerCache;
      })
      .catch(() => { maskerLoading = null; return null; });
  }
  return maskerLoading;
};

function MaskEngine() {
  const { session } = useLiveDemoScene();
  const [masker, setMasker] = useState(() => maskerCache);
  const live = !!session; // lobby counts — the opening slide can precede a live desk by seconds

  // Load once a session is seen and then KEEP masking for this window's whole
  // life — never un-mask on a poll blip or between session end and window
  // close. Privacy fails closed; the projector/mirror state is deliberate and
  // visibly exitable, so lingering a little long costs nothing.
  useEffect(() => {
    if (!live || masker) return undefined;
    let stale = false;
    loadMasker().then((m) => { if (!stale) setMasker(m); });
    return () => { stale = true; };
  }, [live, masker]);

  useLayoutEffect(() => {
    if (!masker) return undefined;
    const { regex, map } = masker;

    const maskText = (node) => {
      const v = node.nodeValue;
      if (!v || v.length < 5) return;
      regex.lastIndex = 0;
      const next = v.replace(regex, (m) => map.get(m.toLowerCase()) || m);
      if (next !== v) node.nodeValue = next;
    };

    // Inputs hold their value as a property, not a text node, so form fields
    // are never rewritten; script/style/editable content is skipped outright.
    const walk = (root) => {
      if (!root) return;
      if (root.nodeType === Node.TEXT_NODE) { maskText(root); return; }
      if (root.nodeType !== Node.ELEMENT_NODE) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => {
          const p = n.parentElement;
          if (!p || p.closest('script, style, textarea, [contenteditable="true"]')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let n;
      while ((n = walker.nextNode())) maskText(n);
    };

    walk(document.body);
    // React re-renders overwrite nodeValue with the real name; the observer
    // re-masks on the same tick, and a masked value no longer matches, so the
    // write-observe cycle always terminates.
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'characterData') maskText(m.target);
        else m.addedNodes.forEach(walk);
      }
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
  }, [masker]);

  return null;
}

export default function StagePrivacyMask() {
  const [mirror, setMirror] = useState(isMirroring);
  // The projector claim is set when the stage page mounts and read per render;
  // subscribing to location keeps the check fresh as that window navigates.
  useLocation();
  useEffect(() => subscribeMirroring(setMirror), []);
  if (!isProjectorWindow() && !mirror) return null;
  return <MaskEngine />;
}
